import { useMemo } from 'react';
import { SOFTWARE_PROJECT } from '@jira/platform__project-types/src/project-types.tsx';
import { createRouterSelector } from '@atlassian/react-resource-router/src/controllers/router-store';
import { useProjectContext } from '@atlassian/jira-providers-project-context/src/index.tsx';
import {
	getIsSoftwareThemingEnabled,
	getIsSoftwareThemingM2Enabled,
	getIsSoftwareThemingM2EnabledNoExposure,
} from '@atlassian/jira-project-theme-providers/src/utils/getIsSoftwareThemingEnabled.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { functionWithCondition } from '@atlassian/jira-feature-flagging-utils/src/utils/function-with-condition/index.tsx';
import {
	getSoftwareThemeRoutes,
	getSoftwareThemeRoutesM2,
	getSoftwareFullThemeRoutesM2,
	getSoftwareFullThemeRoutes,
} from './constants.tsx';

type SoftwareProjectTheming = {
	hasSoftwareTheming: boolean;
	isSoftwareFullTheming: boolean;
	isSoftwareProject: boolean;
};

const defaultValues: SoftwareProjectTheming = {
	hasSoftwareTheming: false,
	isSoftwareFullTheming: false,
	isSoftwareProject: false,
};

export const useCurrentRoute = createRouterSelector((state) => state.route.name);
export const useCurrentUrl = createRouterSelector((state) => state.match.url);

export const useSoftwareProjectThemingOld = (): SoftwareProjectTheming => {
	if (!getIsSoftwareThemingEnabled()) {
		return defaultValues;
	}

	// eslint-disable-next-line react-hooks/rules-of-hooks
	const { data: projectData } = useProjectContext();

	// eslint-disable-next-line react-hooks/rules-of-hooks
	const currentRoute = useCurrentRoute();

	const isSoftwareProject = projectData?.projectType === SOFTWARE_PROJECT;

	if (!isSoftwareProject) {
		return defaultValues;
	}

	// temporarily disable theming for calendar
	// the logic matches with the one in src/packages/platform/project-theme/page-container/src/controllers/project-theme/index.tsx
	const isSoftwareCalendarRoute =
		currentRoute === 'software-calendar' || currentRoute === 'software-calendar-classic';

	if (isSoftwareCalendarRoute && !getIsSoftwareThemingM2Enabled()) {
		return {
			hasSoftwareTheming: false,
			isSoftwareFullTheming: false,
			isSoftwareProject,
		};
	}

	const isM2Enabled = getIsSoftwareThemingM2EnabledNoExposure();

	const hasSoftwareTheming = isM2Enabled
		? isSoftwareProject &&
			getSoftwareThemeRoutesM2().has(currentRoute) &&
			getIsSoftwareThemingM2Enabled() // Only fire exposure if we are on a themed route
		: isSoftwareProject && getSoftwareThemeRoutes().has(currentRoute) && !isSoftwareCalendarRoute;
	const isSoftwareFullTheming = isM2Enabled
		? hasSoftwareTheming && getSoftwareFullThemeRoutesM2().has(currentRoute)
		: hasSoftwareTheming && getSoftwareFullThemeRoutes().has(currentRoute);

	return {
		hasSoftwareTheming,
		isSoftwareFullTheming,
		isSoftwareProject,
	};
};

export const getSoftwareThemedRoutes = () => {
	const allThemedSoftwareProjectRoutes = getIsSoftwareThemingM2EnabledNoExposure()
		? getSoftwareThemeRoutesM2()
		: getSoftwareThemeRoutes();
	const fullThemedSoftwareProjectRoutes = getIsSoftwareThemingM2EnabledNoExposure()
		? getSoftwareFullThemeRoutesM2()
		: getSoftwareFullThemeRoutes();

	// Enable theming for the shared routes no matter which milestone we are in
	allThemedSoftwareProjectRoutes.add('project-pages');
	allThemedSoftwareProjectRoutes.add('project-components');
	fullThemedSoftwareProjectRoutes.add('project-pages');

	return {
		allThemedSoftwareProjectRoutes,
		fullThemedSoftwareProjectRoutes,
	};
};

const useSoftwareProjectThemingSimplified = (): SoftwareProjectTheming => {
	const currentRoute = useCurrentRoute();
	const currentUrl = useCurrentUrl();
	const { allThemedSoftwareProjectRoutes, fullThemedSoftwareProjectRoutes } =
		getSoftwareThemedRoutes();
	const isSoftwareProject =
		allThemedSoftwareProjectRoutes.has(currentRoute) &&
		(currentUrl.includes('/jira/software/projects/') ||
			currentUrl.includes('/jira/software/c/projects/') ||
			currentRoute === 'issue');
	const isSoftwareCalendarRoute =
		currentRoute === 'software-calendar' || currentRoute === 'software-calendar-classic';

	return useMemo(() => {
		if (!isSoftwareProject || !getIsSoftwareThemingEnabled()) {
			return defaultValues;
		}

		// temporarily disable theming for calendar
		// the logic matches with the one in src/packages/platform/project-theme/page-container/src/controllers/project-theme/index.tsx
		if (isSoftwareCalendarRoute && !getIsSoftwareThemingM2Enabled()) {
			return {
				hasSoftwareTheming: false,
				isSoftwareFullTheming: false,
				isSoftwareProject,
			};
		}

		return {
			hasSoftwareTheming: allThemedSoftwareProjectRoutes.has(currentRoute),
			isSoftwareFullTheming: fullThemedSoftwareProjectRoutes.has(currentRoute),
			isSoftwareProject,
		};
	}, [
		isSoftwareProject,
		isSoftwareCalendarRoute,
		allThemedSoftwareProjectRoutes,
		currentRoute,
		fullThemedSoftwareProjectRoutes,
	]);
};

export const useSoftwareProjectTheming = functionWithCondition(
	() => expVal('performance_improvement_in_use_project_theme', 'isEnabled', false),
	useSoftwareProjectThemingSimplified,
	useSoftwareProjectThemingOld,
);
